import React, { useEffect, useState } from "react";
import {
    Plus,
    Trash2,
    Ticket,
    AlertCircle,
    CheckCircle2,
    CalendarDays,
    Percent,
} from 'lucide-react';
import NoImage from "../../../assets/images/no-image.svg";
import BenefitForm from "../benefit/BenefitForm";
import SuspensePopup from "../../SuspensePopup";
import {getBenefit} from "../../../api/benefit/benefit.api";

// 날짜 계산을 위한 헬퍼 함수
const getLocalDateString = (daysToAdd = 0) => {
    const date = new Date();
    date.setDate(date.getDate() + daysToAdd);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// React Component
const EventBenefit = ({
                          benefitSettings,
                          onSelectedBenefit,
                          onDeletedBenefit,
                          onUpdatedBenefit,
                          onValid
                      }) => {
    const [totalPercent, setTotalPercent] = useState("0");
    const [isOpenBenefitPopup, setIsOpenBenefitPopup] = useState(false);
    const [isValidTotal, setValidTotal] = useState(Number(totalPercent) === 100);

    // 통합 업데이트 핸들러
    const handleUpdateBenefit = (id, field, value) => {
        const targetBenefit = benefitSettings.find(b => b.id === id);
        if (!targetBenefit) return;

        const updatedBenefit = { ...targetBenefit, [field]: value };

        // 유효기간 타입이 변경될 때의 연쇄 초기화 및 기본값 설정
        if (field === 'periodType') {
            if (value === 'project_end') {
                updatedBenefit.periodDays = null;
                updatedBenefit.periodDate = null;
            } else if (value === 'date') {
                updatedBenefit.periodDays = null;
                // 날짜 직접 입력 선택 시: 현재일 기준 +14일로 기본 세팅
                updatedBenefit.periodDate = getLocalDateString(14);
            } else if (value === 'issue') {
                updatedBenefit.periodDate = null;
            }
        }

        onUpdatedBenefit(updatedBenefit);
    };

    const maxDecimalPlaces = benefitSettings?.reduce((max, benefit) => {
        const num = benefit.winningPercent ?? 0;
        const decimalPlaces = (num.toString().split(".")[1] || "").length;
        return Math.max(max, decimalPlaces);
    }, 0);

    // 데이터 초기화 로직: periodType이 없으면 'issue'로 강제 업데이트
    useEffect(() => {
        if (!benefitSettings) return;

        benefitSettings.forEach((benefit) => {
            if (!benefit.periodType) {
                handleUpdateBenefit(benefit.id, 'periodType', 'issue');
            }
        });
    }, [benefitSettings]);

    // 총 확률 계산
    useEffect(() => {
        let sumPercent = 0;

        benefitSettings?.forEach((benefit) => {
            if (benefit.winningPercent && benefit.winningPercent !== '') {
                sumPercent += parseFloat(benefit.winningPercent);
            }
        });

        setTotalPercent(sumPercent.toFixed(maxDecimalPlaces));
    }, [benefitSettings]);

    useEffect(() => {
        setValidTotal(Number(totalPercent) === 100);
    }, [totalPercent]);

    // 부모 컴포넌트로 유효성 결과 전달
    useEffect(() => {
        // 날짜 직접 입력에 대한 유효성 검사 추가
        const isConfigValid = !benefitSettings?.find(settings => {
            if (settings.type === "KAKAO" || !settings.id) return false;

            // 발급일 기준인데 일수가 없거나 0 이하일 경우 Invalid
            if (settings.periodType === 'issue' && (!settings.periodDays || settings.periodDays <= 0)) return true;
            // 날짜 직접 입력인데 날짜가 없을 경우 Invalid
            if (settings.periodType === 'date' && !settings.periodDate) return true;

            return false;
        });

        const isEmptyValid = !benefitSettings?.find(settings =>
            settings.type !== "KAKAO" && !settings.id
        );

        onValid(isValidTotal && isConfigValid && isEmptyValid);
    }, [benefitSettings, isValidTotal, onValid]);

    // 혜택 추가 핸들러
    const handleAddBenefit = () => {
        setIsOpenBenefitPopup(true)
    };

    return (
        <div className="w-full bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* 헤더 섹션 */}
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <Ticket className="w-5 h-5 text-indigo-600" />
                    혜택 설정
                </h2>
                <button
                    onClick={handleAddBenefit}
                    className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    혜택 추가
                </button>
            </div>

            {/* 상태 요약 바 */}
            <div className="px-6 py-4 bg-white border-b border-gray-100">
                <div className={`flex items-center justify-between p-3 rounded-lg border ${isValidTotal ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center gap-3">
                        {isValidTotal ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                            <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className={`text-sm font-medium ${isValidTotal ? 'text-green-800' : 'text-red-800'}`}>
                            {isValidTotal ? "설정이 완료되었습니다." : "당첨 확률의 합계가 100%여야 합니다."}
                        </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                        <div className="text-blue-500">
                            남은 확률: <span className="font-bold text-blue-500">{(100 - totalPercent).toFixed(maxDecimalPlaces)}%</span>
                        </div>
                        <div className="text-gray-600">
                            총 확률: <span className={`font-bold ${isValidTotal ? 'text-green-600' : 'text-red-600'}`}>{Number(totalPercent)}%</span> / 100%
                        </div>
                    </div>
                </div>
            </div>

            {/* 리스트 헤더 */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                <div className="col-span-4">혜택 정보 및 메시지</div>
                <div className="col-span-4 text-center">유효기간 설정</div>
                <div className="col-span-3 text-center">당첨 확률</div>
                <div className="col-span-1 text-center">관리</div>
            </div>

            {/* 혜택 리스트 */}
            <div className="divide-y divide-gray-100">
                {benefitSettings?.map((benefit) => (
                    <div key={benefit.id} className="p-6 md:grid md:grid-cols-12 md:gap-6 md:items-start hover:bg-gray-50 transition-colors group">

                        {/* 1. 혜택 정보 & 메시지 설정 */}
                        <div className="col-span-4 space-y-3 mb-4 md:mb-0">
                            <div className="flex items-start gap-3">
                                <div className="w-16 h-16 rounded-lg border border-gray-200 overflow-hidden bg-gray-100 shrink-0">
                                    <img
                                        src={benefit.prizeImage ?? NoImage}
                                        alt="혜택 이미지"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-sm font-bold text-gray-900 truncate">{benefit.name}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 2. 유효기간 설정 (드롭다운 + 입력창) */}
                        <div className="col-span-4 flex flex-col justify-center items-center mb-4 md:mb-0">
                            <div className="w-full max-w-xs bg-white rounded-lg border border-gray-200 p-3">
                                <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-gray-500">
                                    <CalendarDays className="w-3 h-3" />
                                    유효기간 타입
                                </div>
                                {/*
                                  🔴 카카오 선물하기는 유효기간을 오모플레이에서 정할 수 없다.
                                     ① 발송 요청(KakaoGiftBizService.presentGift)에 유효기간 필드가 없다
                                     ② 실제 기프티콘 기간은 카카오에 등록된 상품에 붙어 있고, 우리 DB(kakao_gift)에는
                                        유효기간 컬럼조차 없다
                                     ③ 서버는 타입만 보고 카카오 정책값 93일을 기록한다(EventExternalService)
                                  종전에는 이 선택기를 그대로 노출해 담당자가 3~7일로 설정하면 반영된다고 믿게 만들었다.
                                  선택기를 잠그고 실제 동작을 그 자리에서 알려준다(2026-09-03).
                                */}
                                {benefit.type === "KAKAO" ? (
                                    <>
                                        <select
                                            value="kakao_gift"
                                            disabled
                                            className="w-full text-sm border-gray-300 rounded-md shadow-sm py-1.5 mb-2 bg-gray-100 text-gray-500 cursor-not-allowed"
                                        >
                                            <option value="kakao_gift">카카오 선물하기 기준 (93일 고정)</option>
                                        </select>
                                        <p className="text-xs text-gray-500 leading-relaxed">
                                            카카오 선물하기 혜택은 당첨 즉시 발송되며 카카오 선물하기의 유효기간 기준(93일)이 적용됩니다.
                                        </p>
                                        <p className="mt-1 text-xs font-semibold text-amber-700 leading-relaxed">
                                            주의: 카카오 선물하기 혜택은 프로젝트 비밀번호 설정과 관계없이 즉시 발송됩니다.
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <select
                                            value={benefit.periodType || "issue"}
                                            onChange={(e) => handleUpdateBenefit(benefit.id, 'periodType', e.target.value)}
                                            className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-1.5 mb-2"
                                        >
                                            <option value="issue">발급일 기준 설정</option>
                                            <option value="date">날짜 직접 입력</option>
                                            <option value="project_end">프로젝트 종료 시까지</option>
                                        </select>

                                        {/* 발급일 기준 입력창 */}
                                        {(benefit.periodType === 'issue' || !benefit.periodType) && (
                                            <div className="flex items-center gap-2 animate-fadeIn w-full">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={benefit.periodDays || ""}
                                                    onChange={(e) => handleUpdateBenefit(benefit.id, 'periodDays', parseInt(e.target.value) || 0)}
                                                    className="flex-1 text-center text-sm border border-gray-300 rounded-md py-1.5 focus:ring-2 focus:ring-indigo-500 outline-none w-full"
                                                    placeholder="일수"
                                                />
                                                <span className="text-xs text-gray-600 whitespace-nowrap">일간 사용</span>
                                            </div>
                                        )}

                                        {/* 날짜 직접 입력 선택 시 노출 */}
                                        {benefit.periodType === 'date' && (
                                            <div className="flex items-center gap-2 animate-fadeIn w-full">
                                                <input
                                                    type="date"
                                                    min={getLocalDateString(0)} // 오늘 이전 날짜 선택 불가
                                                    value={benefit.periodDate || ""} // 기본값은 handleUpdateBenefit에서 세팅됨
                                                    onChange={(e) => handleUpdateBenefit(benefit.id, 'periodDate', e.target.value)}
                                                    className="flex-1 text-center text-sm border border-gray-300 rounded-md py-1.5 px-2 focus:ring-2 focus:ring-indigo-500 outline-none w-full"
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* 3. 당첨 확률 */}
                        <div className="col-span-3 flex flex-col justify-center items-center mb-4 md:mb-0 h-full">
                            <div className="relative w-full max-w-[120px]">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Percent className="h-3 w-3 text-gray-400" />
                                </div>
                                <input
                                    type="number"
                                    placeholder="0"
                                    value={benefit.winningPercent ?? 0}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const winning = value.lastIndexOf(".") === value.length - 1 ? value : Number(value);
                                        handleUpdateBenefit(benefit.id, 'winningPercent', winning);
                                    }}
                                    className={`w-full pl-8 pr-3 py-2 text-center font-bold rounded-lg border focus:ring-2 focus:outline-none transition-all
                                    ${Number(benefit.winningPercent ?? 0) >= 0
                                        ? 'border-indigo-200 bg-indigo-50 text-indigo-700 focus:border-indigo-500'
                                        : 'border-gray-200 text-gray-500 focus:border-indigo-500'}`}
                                />
                            </div>
                        </div>

                        {/* 4. 삭제 버튼 */}
                        <div className="col-span-1 flex justify-center items-center h-full">
                            <button
                                onClick={() => onDeletedBenefit(benefit.id)}
                                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                                title="삭제"
                            >
                                <Trash2 className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                ))}

                {/* 데이터 없음 처리 */}
                {(!benefitSettings || benefitSettings.length === 0) && (
                    <div className="py-12 text-center">
                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Ticket className="w-8 h-8 text-gray-300" />
                        </div>
                        <p className="text-gray-500 text-sm">등록된 혜택이 없습니다.</p>
                    </div>
                )}
            </div>
            <SuspensePopup title={"혜택 연결하기"} visible={isOpenBenefitPopup}
                           onClose={() => setIsOpenBenefitPopup(false)}
            >
                <BenefitForm
                    onSelectedBenefit={(benefitId) => {
                        if (!benefitSettings?.some(benefit => benefit.id === benefitId)) {
                            getBenefit(benefitId).then(({data}) => {
                                const newBenefit = { ...data, periodType: 'issue' };
                                onSelectedBenefit(newBenefit)
                            })
                        }
                        setIsOpenBenefitPopup(false)
                    }}
                />
            </SuspensePopup>
        </div>
    );
};

export default EventBenefit;